import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios"; // make sure Axios is imported from your custom instance
import CancelBackButton from "components/Buttons/CancelBackButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
import { autocompleteDropdownStyles } from "Constants/Style";
import React, { useEffect, useState } from "react";
import { Col, Form, ListGroup, Row } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addModuels,
  updateModules as onUpdateModule,
} from "slices/modules/thunk";
import { AppDispatch } from "slices/store";

const permissionOptions = ["View", "Create", "Update"];

const AddModuleForm = () => {
  const navigate = useNavigate();
  const { pathname, state }: any = useLocation();
  const isEdit = pathname === "/edit-module-form";
  const dispatch = useDispatch<AppDispatch>();

  const [appName, setAppName] = useState("");
  const [newModuleName, setNewModuleName] = useState("");
  const [newModuleActions, setNewModuleActions] = useState<string[]>([]);
  const [modules, setModules] = useState<
    { module_name: string; actions: string[] }[]
  >([]);
  const [existingAppNames, setExistingAppNames] = useState<string[]>([]);

  const [errors, setErrors] = useState<{
    appName?: string;
    newModuleName?: string;
    newModuleActions?: string;
    modules?: string;
  }>({});

  useEffect(() => {
    // For edit mode - populate values
    if (isEdit && state) {
      setAppName(state.app_name || "");
      const initialModules = Object.entries(state.module_name_perm || {}).map(
        ([module_name, actions]) => ({
          module_name,
          actions: actions as string[],
        })
      );
      setModules(initialModules);
    }

    // Fetch existing app names (for validation)
    const fetchExistingApps = async () => {
      try {
        const res = await Axios.get("/modules/apps/");
        const appNames = res.data.map((app: any) =>
          app.app_name?.toLowerCase()
        );
        setExistingAppNames(appNames || []);
      } catch (err) {
        console.error("Failed to fetch app names", err);
      }
    };

    fetchExistingApps();
  }, [isEdit, state]);

  const validateAppName = () => {
    const trimmedName = appName.trim();

    if (!trimmedName) {
      setErrors((e) => ({ ...e, appName: "Please enter App Name" }));
      return false;
    }

    const isDuplicate = existingAppNames.includes(trimmedName.toLowerCase());

    if (!isEdit && isDuplicate) {
      setErrors((e) => ({ ...e, appName: "App Name already exists" }));
      return false;
    }

    setErrors((e) => ({ ...e, appName: undefined }));
    return true;
  };

  const validateNewModule = () => {
    let valid = true;

    if (!newModuleName.trim()) {
      setErrors((e) => ({ ...e, newModuleName: "Please enter Module Name" }));
      valid = false;
    } else if (
      modules.some(
        (mod) =>
          mod.module_name.trim().toLowerCase() ===
          newModuleName.trim().toLowerCase()
      )
    ) {
      setErrors((e) => ({ ...e, newModuleName: "Module name already exists" }));
      valid = false;
    } else {
      setErrors((e) => ({ ...e, newModuleName: undefined }));
    }

    if (newModuleActions.length < 1) {
      setErrors((e) => ({
        ...e,
        newModuleActions: "Please select at least one permission",
      }));
      valid = false;
    } else {
      setErrors((e) => ({ ...e, newModuleActions: undefined }));
    }

    return valid;
  };

  const validateModulesList = () => {
    if (modules.length === 0) {
      setErrors((e) => ({
        ...e,
        modules: "Please add at least one module",
      }));
      return false;
    }
    setErrors((e) => ({ ...e, modules: undefined }));
    return true;
  };

  const handleAddModule = () => {
    if (!validateNewModule()) return;

    setModules([
      ...modules,
      { module_name: newModuleName.trim(), actions: newModuleActions },
    ]);
    setNewModuleName("");
    setNewModuleActions([]);
  };

  const handleRemoveModule = (index: number) => {
    const updated = [...modules];
    updated.splice(index, 1);
    setModules(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isAppNameValid = validateAppName();
    const isModulesListValid = validateModulesList();

    if (!isAppNameValid || !isModulesListValid) return;

    const module_name_perm: Record<string, string[]> = modules.reduce(
      (acc, mod) => {
        acc[mod.module_name] = mod.actions;
        return acc;
      },
      {} as Record<string, string[]>
    );

    const payload = {
      app_name: appName.trim(),
      module_name_perm,
    };

    try {
      if (isEdit) {
        await dispatch(
          onUpdateModule({ id: state?.modulemaster_id, ...payload })
        ).unwrap();
        toast.success("Module updated successfully");
      } else {
        await dispatch(addModuels(payload)).unwrap();
        toast.success("Module added successfully");
      }
      navigate("/module-list", { state: { clearState: !isEdit } });
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const backendMessage = data?.message || data?.error || error?.message;

      if (status === 404 && backendMessage) {
        toast.error(backendMessage);
      } else if (
        status === 409 ||
        backendMessage?.toLowerCase().includes("exist")
      ) {
        toast.error(backendMessage);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <PageContainer>
      <FormHeader
        title={`${isEdit ? "Edit" : "Add"} Module`}
        firstLabel="Module"
        secondLabel={isEdit ? "Edit Module" : "Add Module"}
        handleArrowClick={() => navigate("/module-list")}
      />

      <Form onSubmit={handleSubmit}>
        <Row className="mb-4">
          <Col md={6}>
            <Form.Group controlId="appName">
              <Form.Label className="fw-semibold">
                App Name
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter App Name"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                onBlur={validateAppName}
                isInvalid={!!errors.appName}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.appName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="moduleName">
              <Form.Label className="fw-semibold">
                Module Name
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Module Name"
                value={newModuleName}
                onChange={(e) => setNewModuleName(e.target.value)}
                onBlur={validateNewModule}
                isInvalid={!!errors.newModuleName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.newModuleName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3 align-items-end">
          <Col md={6}>
            <Form.Group controlId="permissions">
              <Form.Label className="fw-semibold">
                Permissions
                <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                multiple
                options={permissionOptions}
                value={newModuleActions}
                onChange={(_, newValue) => setNewModuleActions(newValue)}
                onBlur={validateNewModule}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      newModuleActions?.length === 0 ? "Select permissions" : ""
                    }
                    size="small"
                    error={!!errors.newModuleActions}
                    helperText={errors.newModuleActions}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                  />
                )}
                disableCloseOnSelect
              />
            </Form.Group>
          </Col>
          {/* </Row>

          <Row className="my-4 justify-content-center cursor-pointer"> */}
          <Col md="auto">
            {/* <Button
                variant="primary"
                onClick={handleAddModule}
                className="d-flex align-items-center gap-2 px-4 py-2 fw-semibold"
                type="button"
                disabled={
                  !newModuleName.trim() ||
                  modules.some(
                    (mod) =>
                      mod.module_name.trim().toLowerCase() ===
                      newModuleName.trim().toLowerCase()
                  )
                }
              >
                <BsPlus size={20} />
                Add
              </Button> */}

            <SubmitButton
              onClick={handleAddModule}
              isSubmitting={false}
              labels={{
                create: "Add Module into List",
                submitting: "Adding...",
              }}
              disabled={
                !newModuleName.trim() ||
                modules.some(
                  (mod) =>
                    mod.module_name.trim().toLowerCase() ===
                    newModuleName.trim().toLowerCase()
                )
              }
              isEdit={isEdit}
            />
          </Col>
        </Row>

        {modules.length > 0 && (
          <div>
            <h6 className="mt-5">Added Modules</h6>
            <ListGroup className="mb-4">
              {modules.map((mod, index) => (
                <ListGroup.Item
                  key={index}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <strong>{mod.module_name}</strong> - Permissions:{" "}
                    {mod.actions.join(", ")}
                  </div>
                  <DeleteButton
                    onClick={() => handleRemoveModule(index)}
                    iconClassName="bi bi-trash"
                    isLoading={false}
                    children={<BsTrash />}
                  />
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}
        {errors.modules && (
          <div className="text-danger mb-3">{errors.modules}</div>
        )}

        <Row className="mt-2">
          <Col className="d-flex justify-content-center gap-3 ">
            <CancelBackButton to="/module-list" />
            <SubmitButton
              isSubmitting={false}
              onClick={handleSubmit}
              labels={{
                create: "Add Module",
                update: "Update Module",
                submitting: "Adding...",
                updating: "Updating...",
              }}
              isEdit={isEdit}
            />
          </Col>
        </Row>
      </Form>
    </PageContainer>
  );
};

export default AddModuleForm;
